import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import client from '../api/client';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function CoupangTracker() {
  const [trackers, setTrackers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<{[key: number]: boolean}>({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await client.get('/coupang/auto-track-list');
      setTrackers(res.data.trackers || []);
      const initCollapsed: {[key: number]: boolean} = {};
      res.data.trackers.forEach((t: any) => { initCollapsed[t.id] = true; });
      setCollapsed(initCollapsed);
    } catch {} finally { setLoading(false); }
  };

  const toggleCollapse = (id: number) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  const s = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 } as any,
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
      return (
        <div style={{ background: '#1E2535', border: '0.5px solid #2A3045', borderRadius: 6, padding: '8px 12px' }}>
          <div style={{ color: '#8892A4', fontSize: 11 }}>{label}</div>
          <div style={{ color: '#E8192C', fontSize: 14, fontWeight: 600 }}>{payload[0].value}위</div>
        </div>
      );
    }
    return null;
  };

  const getStats = (history: any[]) => {
    const ranks = history.filter(h => h.coupang_rank).map(h => h.coupang_rank);
    if (!ranks.length) return { best: null, worst: null, avg: null };
    return {
      best: Math.min(...ranks),
      worst: Math.max(...ranks),
      avg: Math.round(ranks.reduce((a, b) => a + b, 0) / ranks.length)
    };
  };

  const getAlerts = (keywords: any[]) => keywords.filter(kw => {
    if (!kw.cur_rank || !kw.prev_rank) return false;
    return Math.abs(kw.cur_rank - kw.prev_rank) >= 5;
  });

  const getSummary = (keywords: any[]) => {
    const ranks = keywords.filter(k => k.cur_rank).map(k => k.cur_rank);
    return {
      bestRank: ranks.length > 0 ? Math.min(...ranks) : null,
      alertCount: getAlerts(keywords).length,
      totalKeywords: keywords.length
    };
  };

  const rankColor = (rank: number | null) => {
    if (!rank) return '#F56565';
    if (rank <= 3) return '#F6AD55';
    if (rank <= 10) return '#E8192C';
    if (rank <= 30) return '#378ADD';
    return '#8892A4';
  };

  return (
    <Layout>
      <div>
        <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>쿠팡 순위 변동 추적</h2>
        <p style={{ color: '#8892A4', fontSize: 12, margin: '0 0 8px' }}>자동순위추적 ON 상품의 키워드별 순위 변동을 확인하세요</p>
        <div style={{ background: '#1A2035', border: '1px solid #E8192C55', borderRadius: 6, padding: '8px 14px', marginBottom: 20, display: 'inline-block' }}>
          <span style={{ color: '#E8192C', fontSize: 12 }}>💡 순위 변동 자동 추적은 <strong>쿠팡 순위 조회</strong> 메뉴에서 추가 · 삭제할 수 있습니다</span>
        </div>

        {loading && <div style={{ color: '#8892A4', fontSize: 14 }}>불러오는 중...</div>}

        {!loading && trackers.length === 0 && (
          <div style={{ ...s.card, textAlign: 'center' as const, padding: 40 }}>
            <div style={{ color: '#8892A4', fontSize: 14, marginBottom: 8 }}>자동순위추적 중인 상품이 없어요</div>
            <div style={{ color: '#4A5568', fontSize: 12 }}>쿠팡 순위 조회 메뉴에서 "자동순위추적" 버튼을 켜주세요</div>
          </div>
        )}

        {trackers.map(t => {
          const isCollapsed = collapsed[t.id];
          const alerts = getAlerts(t.keywords);
          const summary = getSummary(t.keywords);

          return (
            <div key={t.id} style={s.card}>
              {/* 헤더 */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: isCollapsed ? 0 : 16, paddingBottom: isCollapsed ? 0 : 16, borderBottom: isCollapsed ? 'none' : '0.5px solid #2A3045' }}>
                {t.thumbnail ? (
                  <img src={t.thumbnail} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: 6, background: '#0F1117', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 20 }}>🛒</span>
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{t.product_name || `상품 ${t.product_id}`}</div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ color: '#4A5568', fontSize: 12 }}>키워드 {summary.totalKeywords}개</span>
                    {summary.bestRank && <span style={{ color: '#E8192C', fontSize: 12 }}>최고 {summary.bestRank}위</span>}
                    {summary.alertCount > 0 && (
                      <span style={{ background: '#2A1A1A', color: '#F56565', fontSize: 11, padding: '2px 8px', borderRadius: 4 }}>
                        급변동 {summary.alertCount}개
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => toggleCollapse(t.id)}
                  style={{ background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '4px 12px', color: '#8892A4', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                  {isCollapsed ? '펼치기 ▼' : '접기 ▲'}
                </button>
              </div>

              {!isCollapsed && (
                <>
                  {/* 급변동 알림 */}
                  {alerts.length > 0 && (
                    <div style={{ background: '#1A1A2A', border: '0.5px solid #2A2A4A', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                      <div style={{ color: '#8892A4', fontSize: 11, marginBottom: 8 }}>⚡ 급변동 알림 (5위 이상 변동)</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 8 }}>
                        {alerts.map((kw: any) => {
                          const diff = kw.prev_rank - kw.cur_rank;
                          return (
                            <div key={kw.id} style={{ background: diff > 0 ? '#0F2A22' : '#2A1A1A', borderRadius: 6, padding: '4px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span style={{ color: '#E8ECF4', fontSize: 12 }}>{kw.keyword}</span>
                              <span style={{ color: diff > 0 ? '#00D4AA' : '#F56565', fontSize: 12, fontWeight: 700 }}>
                                {diff > 0 ? `▲ ${diff}` : `▼ ${Math.abs(diff)}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* 키워드 목록 */}
                  {t.keywords.length === 0 ? (
                    <div style={{ color: '#4A5568', fontSize: 13 }}>쿠팡 순위 조회에서 키워드를 추가해주세요</div>
                  ) : t.keywords.map((kw: any) => {
                    const stats = getStats(kw.history);
                    const graphData = [...kw.history].reverse().map((h: any) => ({
                      date: String(h.checked_at).split(' ')[0],
                      rank: h.coupang_rank || null
                    }));
                    const diff = kw.cur_rank && kw.prev_rank ? kw.prev_rank - kw.cur_rank : null;

                    return (
                      <div key={kw.id} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '0.5px solid #2A3045' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{kw.keyword}</span>
                            {diff !== null && (
                              <span style={{ background: (diff > 0 ? '#00D4AA' : '#F56565') + '22', color: diff > 0 ? '#00D4AA' : '#F56565', fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                                {diff > 0 ? `▲ ${diff}` : `▼ ${Math.abs(diff)}`}
                              </span>
                            )}
                          </div>
                          {kw.cur_rank ? (
                            <div style={{ background: '#1A0F0F', borderRadius: 6, padding: '4px 12px', textAlign: 'center' as const }}>
                              <div style={{ fontSize: 10, color: '#4A5568' }}>순위</div>
                              <div style={{ fontSize: 16, fontWeight: 600, color: rankColor(kw.cur_rank) }}>{kw.cur_rank}위</div>
                            </div>
                          ) : (
                            <span style={{ color: '#F56565', fontSize: 13 }}>순위 밖</span>
                          )}
                        </div>

                        {/* 통계 */}
                        {stats.best && (
                          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                            <div style={{ background: '#0F1117', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
                              <span style={{ color: '#4A5568' }}>최고 </span>
                              <span style={{ color: '#E8192C', fontWeight: 600 }}>{stats.best}위</span>
                            </div>
                            <div style={{ background: '#0F1117', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
                              <span style={{ color: '#4A5568' }}>최저 </span>
                              <span style={{ color: '#F56565', fontWeight: 600 }}>{stats.worst}위</span>
                            </div>
                            <div style={{ background: '#0F1117', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
                              <span style={{ color: '#4A5568' }}>평균 </span>
                              <span style={{ color: '#8892A4', fontWeight: 600 }}>{stats.avg}위</span>
                            </div>
                          </div>
                        )}

                        {/* 그래프 */}
                        {graphData.length > 1 && (
                          <div style={{ marginBottom: 12 }}>
                            <ResponsiveContainer width="100%" height={100}>
                              <LineChart data={graphData}>
                                <XAxis dataKey="date" tick={{ fill: '#4A5568', fontSize: 9 }} />
                                <YAxis reversed domain={['dataMin - 2', 'dataMax + 2']} tick={{ fill: '#4A5568', fontSize: 9 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Line type="monotone" dataKey="rank" stroke="#E8192C" strokeWidth={2} dot={{ fill: '#E8192C', r: 3 }} connectNulls={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        {/* 기록 테이블 */}
                        {kw.history.length > 0 && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                            <div style={{ color: '#4A5568', fontSize: 11, paddingBottom: 4 }}>날짜</div>
                            <div style={{ color: '#4A5568', fontSize: 11, paddingBottom: 4, textAlign: 'right' as const }}>순위</div>
                            {kw.history.slice(0, 7).map((h: any, j: number) => (
                              <>
                                <div key={`d${j}`} style={{ color: '#8892A4', fontSize: 12, padding: '2px 0' }}>{String(h.checked_at).split('T')[0]}</div>
                                <div key={`r${j}`} style={{ color: rankColor(h.coupang_rank), fontSize: 12, padding: '2px 0', textAlign: 'right' as const, fontWeight: 600 }}>
                                  {h.coupang_rank ? `${h.coupang_rank}위` : '순위밖'}
                                </div>
                              </>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
